// Utility to dynamically set redirect URLs for AWS Amplify OAuth
import awsmobile from '../config/aws-exports';

/**
 * Gets the current page path from the URL
 * @returns {string} The current path
 */

// Define the basename to match your Router configuration
const BASENAME = '/';
const AUTH_PATHS = new Set(['/anmelden', '/registrieren']);

const stripBasename = (path) => {
  if (!path) return '/';
  if (BASENAME === '/') return path;
  if (path.startsWith(BASENAME)) {
    const withoutBasename = path.slice(BASENAME.length);
    return withoutBasename.startsWith('/') ? withoutBasename : `/${withoutBasename}`;
  }
  return path;
};

export const normalizeRedirectPath = (target, fallback = '/') => {
  const safeFallback = typeof fallback === 'string' && fallback.startsWith('/') && !fallback.startsWith('//')
    ? fallback
    : '/';

  let rawPath = '';

  if (typeof target === 'string') {
    rawPath = target;
  } else if (target && typeof target === 'object') {
    const pathname = typeof target.pathname === 'string' ? target.pathname : '/';
    const search = typeof target.search === 'string' ? target.search : '';
    const hash = typeof target.hash === 'string' ? target.hash : '';
    rawPath = `${pathname}${search}${hash}`;
  }

  if (!rawPath || !rawPath.startsWith('/') || rawPath.startsWith('//')) {
    return safeFallback;
  }

  const normalizedPath = stripBasename(rawPath);
  const pathname = normalizedPath.split(/[?#]/)[0] || '/';

  if (AUTH_PATHS.has(pathname)) {
    return safeFallback;
  }

  return normalizedPath;
};

export const getRedirectPathFromLocationState = (location, fallback = '/') => (
  normalizeRedirectPath(location?.state?.from, fallback)
);

export const getAuthRedirectPath = (location, fallback = '/') => {
  try {
    const storedPath = sessionStorage.getItem('oauth_redirect_path');
    if (storedPath) {
      return getAndClearRedirectPath();
    }
  } catch (error) {
    // Fall back to router state when sessionStorage is unavailable.
  }

  return getRedirectPathFromLocationState(location, fallback);
};

export const getCurrentPath = () => {
  const fullPath = window.location.pathname + window.location.search + window.location.hash;
  return normalizeRedirectPath(fullPath);
};

/**
 * Creates a new Amplify configuration with dynamic redirect URL
 * @param {string} currentPath - The current page path (e.g., '/profile', '/bookings')
 */
export const createDynamicConfig = (currentPath) => {
  const baseUrl = process?.env?.REACT_APP_REDIRECT_SIGN_IN || 'http://localhost:3000';
  const redirectUrl = `${baseUrl}${currentPath}`;
  
  // Create a new config object with the dynamic redirect URL
  const dynamicConfig = {
    Auth: {
      Cognito: {
        region: awsmobile.aws_project_region,
        userPoolId: awsmobile.aws_user_pools_id,
        userPoolClientId: awsmobile.aws_user_pools_web_client_id,
        identityPoolId: awsmobile.aws_cognito_identity_pool_id,
        loginWith: {
          oauth: {
            domain: awsmobile.oauth.domain,
            scopes: awsmobile.oauth.scope,
            redirectSignIn: [
              redirectUrl,
              baseUrl + '/', // fallback to home
              'https://stu-studios.com/',
              'https://www.stu-studios.com/'
            ],
            redirectSignOut: [
              baseUrl + '/',
              'https://stu-studios.com/',
              'https://www.stu-studios.com/'
            ],
            responseType: awsmobile.oauth.responseType,
            providers: ['Google', 'Facebook', 'SignInWithApple']
          }
        }
      }
    },
    API: {
      GraphQL: {
        endpoint: awsmobile.aws_appsync_graphqlEndpoint,
        region: awsmobile.aws_appsync_region,
        defaultAuthMode: awsmobile.aws_appsync_authenticationType
      }
    },
    Storage: {
      S3: {
        bucket: awsmobile.aws_user_files_s3_bucket,
        region: awsmobile.aws_user_files_s3_bucket_region,
        defaultAccessLevel: 'public'
      }
    }
  };
  
  return { dynamicConfig, redirectUrl };
};

/**
 * Stores the current path for redirect after OAuth
 * This is a simpler approach that works with Cognito's redirect restrictions
 */
export const storeRedirectPath = () => {
  const currentPath = getCurrentPath();
  // Only set if not already set by an explicit intent (e.g., Gastgeber button)
  const alreadySet = sessionStorage.getItem('oauth_redirect_path');
  if (!alreadySet) {
    sessionStorage.setItem('oauth_redirect_path', currentPath);
  }

  return alreadySet || currentPath;
};

/**
 * Gets the stored redirect path and clears it
 * Call this after successful OAuth authentication
 */
export const getAndClearRedirectPath = () => {
  const storedPath = sessionStorage.getItem('oauth_redirect_path');
  sessionStorage.removeItem('oauth_redirect_path');
  return normalizeRedirectPath(storedPath);
};

export const clearRedirectPathIfMatches = (target) => {
  try {
    const expectedPath = normalizeRedirectPath(target);
    const storedPath = sessionStorage.getItem('oauth_redirect_path');

    if (storedPath && normalizeRedirectPath(storedPath) === expectedPath) {
      sessionStorage.removeItem('oauth_redirect_path');
      return true;
    }
  } catch (error) {
    // Ignore storage failures; callers should still be able to close UI.
  }

  return false;
};

/**
 * Stores a specific path for redirect after OAuth
 * Use this when you want to force redirect to a target page after login
 * @param {string|object} path - The desired redirect path or location-like object.
 */
export const storeRedirectPathTo = (path) => {
  
  try {
    const safePath = normalizeRedirectPath(path);
    sessionStorage.setItem('oauth_redirect_path', safePath);
    return safePath;
  } catch (error) {
    // If sessionStorage is unavailable, silently fail and return path
    return path;
  }
};

/**
 * Ensures a redirect path is set only if none exists
 * @param {string} path
 */
export const ensureRedirectPath = (path) => {
  try {
    const existing = sessionStorage.getItem('oauth_redirect_path');
    if (!existing) {
      const safePath = normalizeRedirectPath(path);
      sessionStorage.setItem('oauth_redirect_path', safePath);
      return safePath;
    }
    return normalizeRedirectPath(existing);
  } catch (error) {
    return normalizeRedirectPath(path);
  }
};

/**
 * Sets up redirect for OAuth flows (simplified version)
 * Call this before initiating OAuth sign-in/sign-up
 */
export const setupOAuthRedirect = () => {
  // Just store the current path, don't modify Amplify config
  return storeRedirectPath();
};
